/**
 * Audio Manager "Find transcript" — for ONE chart audio with no transcript.
 *
 * Asks POST /api/transcription-backup/find-transcript/ where its transcript
 * might be (nothing written): an unlinked transcript on the same chart whose
 * file name or Audio number is this recording's, a transcript on another chart
 * with its url / session, or a transcription-backups JSON for it in its day
 * folder (any provider). Anything naming another patient isn't returned. Each
 * candidate shows why it matched and its text; "Link to this audio" sends that
 * candidate's id, and the server re-runs the search before writing, so a stale
 * list can't link something that changed.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { supabasePublic } from "../../components/supabaseClient";
import { getBackendURL } from "../../components/env";
import AudioPlayer from "../../components/table/audio/AudioPlayer";
import { clearBackupCache } from "../../components/AudioManagerModal";

const KIND_LABEL = {
  chart: "On this chart",
  other_chart: "On another chart",
  backup_json: "Transcript backup",
};
const CONF_COLOR = { high: "green", medium: "yellow" };

/** What the server needs to find the more_audios entry again. */
export const audioRefOf = (audio) => ({
  url: audio?.url || audio?.audio_url || null,
  bucket: audio?.bucket || null,
  path: audio?.path || audio?.storage_path || null,
});

async function callFind(body) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const resp = await fetch(`${getBackendURL()}/api/transcription-backup/find-transcript/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const result = await resp.json().catch(() => ({}));
  if (result.error === "doctor_role_required") throw new Error("Doctor role required to link transcripts.");
  if (!resp.ok || !result.success) throw new Error(result.error || `Search failed (${resp.status})`);
  return result;
}

function CandidateCard({ c, busy, disabled, onLink }) {
  const [full, setFull] = useState(false);
  const clipped = c.text_length > c.preview.length;
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <HStack spacing={2} mb={1} wrap="wrap">
        <Badge colorScheme="purple">{KIND_LABEL[c.kind] || c.kind}</Badge>
        <Badge colorScheme={CONF_COLOR[c.confidence] || "gray"}>{c.confidence}</Badge>
        {c.created_at && (
          <Text fontSize="xs" color="gray.500">{new Date(c.created_at).toLocaleString()}</Text>
        )}
        <Text fontSize="xs" color="gray.500">{c.text_length.toLocaleString()} characters</Text>
      </HStack>
      <Text fontSize="xs" color="gray.600" wordBreak="break-all">{c.source_label}</Text>
      <Text fontSize="sm" mt={1}>{c.reason}</Text>
      <Box mt={2} p={2} bg="gray.50" borderRadius="sm" maxH={full ? "360px" : "120px"} overflowY="auto">
        <Text fontSize="xs" whiteSpace="pre-wrap" color="gray.700">
          {c.preview}
          {clipped && "…"}
        </Text>
      </Box>
      <Flex mt={2} justify="space-between" align="center">
        {c.preview.length > 200 ? (
          <Button size="xs" variant="link" onClick={() => setFull((v) => !v)}>
            {full ? "Less" : "More"}
          </Button>
        ) : (
          <Box />
        )}
        <Button size="sm" colorScheme="green" isLoading={busy} isDisabled={disabled} onClick={() => onLink(c)}>
          Link to this audio
        </Button>
      </Flex>
    </Box>
  );
}

export default function FindTranscriptDialog({ isOpen, onClose, row, audio, schemas, label, onDone }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [linking, setLinking] = useState(null);

  const base = useCallback(
    () => ({ schema: row?._schema, patient_id: row?.id, audio: audioRefOf(audio), schemas: schemas || [] }),
    [row, audio, schemas],
  );

  const search = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setResult(await callFind(base()));
    } catch (err) {
      setResult(null);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    if (isOpen) search();
  }, [isOpen, search]);

  const link = async (c) => {
    setLinking(c.id);
    try {
      await callFind({ ...base(), link_id: c.id });
      clearBackupCache(row?.id);
      toast({ title: "Transcript linked", status: "success", duration: 3000 });
      await onDone?.();
      onClose();
    } catch (err) {
      toast({ title: "Link failed", description: err?.message || String(err), status: "error", duration: 6000, isClosable: true });
      await search();
    } finally {
      setLinking(null);
    }
  };

  const candidates = result?.candidates || [];
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Find transcript
          <Text fontSize="sm" fontWeight="normal" color="gray.600" wordBreak="break-all">
            {row?.patient_name || "Patient"} · {row?._schema} · {label || audio?.displayName || audio?.name}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            {audio?.url && (
              <Flex justify="center" overflowX="auto">
                <AudioPlayer audioUrl={audio.url} audio_duration={audio.duration} patientId={row?.id} audioDisplayName={label} />
              </Flex>
            )}
            {loading ? (
              <Flex justify="center" py={8} gap={3} align="center">
                <Spinner />
                <Text color="gray.600">Searching charts and transcript backups…</Text>
              </Flex>
            ) : error ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            ) : result?.has_transcript ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                This recording already has a transcript on the chart. Refresh the Audio Manager to see it.
              </Alert>
            ) : candidates.length === 0 ? (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                No transcript found for this recording. Searched this chart, every chart that names it, and
                transcript backups for {(result?.searched_days || []).join(", ") || "its day"} in{" "}
                {(result?.searched_schemas || []).length} provider folder
                {(result?.searched_schemas || []).length === 1 ? "" : "s"}.
              </Alert>
            ) : (
              <>
                <Text fontSize="sm" color="gray.600">
                  {candidates.length} possible transcript{candidates.length === 1 ? "" : "s"}, best match first. Read
                  it, then link the right one — only that one is written.
                </Text>
                {candidates.map((c) => (
                  <CandidateCard
                    key={c.id}
                    c={c}
                    busy={linking === c.id}
                    disabled={!!linking && linking !== c.id}
                    onLink={link}
                  />
                ))}
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="outline" onClick={search} isLoading={loading}>
            Search again
          </Button>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
